import axios from 'axios';
import { contractorToken } from './contractorAuth';
import { viewingContractorId } from './viewAs';

/**
 * Announcements (migration 125) from the browser: the calls, and the words
 * every announcement screen uses - the banner, the Announcements tab under
 * the bell, Advanced -> Announcements, and the staff notice's line on the
 * Retention page.
 *
 * READING is anybody's: staff on their session (lib/apiClient adds the
 * token), a contractor on the portal's own token - passed here, because the
 * portal's requests do not go through the Entra interceptor - and an
 * administrator previewing a contractor's portal names them (as=c:<id>), the
 * way the to-do page does, and is shown their feed read-only. WRITING is an
 * administrator's; the server refuses everybody else whatever this does.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** The reader's door: a contractor's bearer, or (previewing) whose portal. */
export function readerConfig(token) {
  const t = token === undefined ? null : token;
  const params = {};
  const previewing = !t && viewingContractorId();
  if (previewing) params.as = `c:${previewing}`;
  return { ...(t ? { headers: { Authorization: `Bearer ${t}` } } : {}), params };
}

/** The token the navbar's bell uses, worked out the same way for the banner. */
export const portalToken = (signedInStaff) => (!signedInStaff ? contractorToken() : null);

export const announcementsApi = {
  mine: (token) => axios.get(`${API}/announcements/mine`, readerConfig(token)).then((r) => r.data),
  read: (id, token) => axios.post(`${API}/announcements/${id}/read`, {}, readerConfig(token)).then((r) => r.data),
  acknowledge: (id, version, token) => axios.post(`${API}/announcements/${id}/acknowledge`, { version }, readerConfig(token)).then((r) => r.data),

  list: () => axios.get(`${API}/announcements`).then((r) => r.data),
  options: () => axios.get(`${API}/announcements/options`).then((r) => r.data),
  get: (id) => axios.get(`${API}/announcements/${id}`).then((r) => r.data),
  create: (body) => axios.post(`${API}/announcements`, body).then((r) => r.data),
  update: (id, body) => axios.patch(`${API}/announcements/${id}`, body).then((r) => r.data),
  end: (id) => axios.post(`${API}/announcements/${id}/end`).then((r) => r.data),
  remind: (id) => axios.post(`${API}/announcements/${id}/remind`).then((r) => r.data),
  people: (id) => axios.get(`${API}/announcements/${id}/people`).then((r) => r.data)
};

/** The sentence a refusal carries, or `fallback`. */
export const errorOf = (e, fallback = 'That did not work.') => e?.response?.data?.error || fallback;

/** Something changed (read, acknowledged, published): every reader looks again. */
export const announceChanged = () => {
  try { window.dispatchEvent(new CustomEvent('jarvis:announcements-changed')); } catch { /* old browser */ }
};

export const IMPORTANCE_LABEL = { info: 'Info', important: 'Important' };
export const STATE_LABEL = { scheduled: 'Scheduled', live: 'Showing', ended: 'Ended' };
export const stateTone = (s) => ({ scheduled: 'info', live: 'success', ended: 'idle' }[s] || 'idle');

export const AUDIENCE_LABEL = {
  everyone: 'Everyone',
  staff: 'Staff only',
  contractors: 'Contractors only',
  departments: 'One or more departments',
  people: 'Named people'
};

/** 26 Oct 2026, 09:40. */
export const when = (d) => (d ? new Date(d).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
}).replace(/ at /, ', ') : '');
/** 26 Oct 2026. */
export const day = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/** For <input type="datetime-local">: the browser's local time, to the minute. */
export function toLocalInput(d) {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
}
/** From it: an instant, or null for empty. */
export const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

/** "4 of 6 have acknowledged it", "2 of 6 have read it". */
export function doneWords(a) {
  const c = a?.counts;
  if (!c) return '';
  return a.importance === 'important'
    ? `${c.acknowledged} of ${c.total} acknowledged`
    : `${c.read} of ${c.total} read`;
}
